import React from 'react';
import { Box, ButtonBase, Typography, alpha, styled } from '@mui/material';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { RoleDisplayConfig } from './helpers';

interface RoleCardProps {
    display: RoleDisplayConfig;
    userCount: number;
    accessiblePages: number;
    totalPages: number;
    onClick?: () => void;
}

const Card = styled(ButtonBase)(({ theme }) => ({
    width: '100%',
    height: '100%',
    minHeight: theme.spacing(18),
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.common.white,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    justifyContent: 'flex-start',
    textAlign: 'left',
    transition: 'border-color 0.15s, box-shadow 0.15s',
    '&:hover': {
        borderColor: theme.palette.primary.main,
        boxShadow: theme.shadows[1]
    }
}));

const HeaderRow = styled(Box)({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%'
});

const IconAndTitle = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1)
}));

const IconWrapper = styled(Box)<{ tint: string }>(({ tint }) => ({
    width: 28,
    height: 28,
    borderRadius: 6,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: tint,
    backgroundColor: alpha(tint, 0.12)
}));

const Description = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(1),
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden'
}));

const Footer = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginTop: 'auto',
    paddingTop: theme.spacing(2),
    color: theme.palette.text.secondary
}));

const FooterItem = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    fontSize: theme.typography.body2.fontSize
}));

export const RoleCard: React.FC<RoleCardProps> = ({
    display,
    userCount,
    accessiblePages,
    totalPages,
    onClick
}) => {
    const Icon = display.icon;
    return (
        <Card onClick={onClick} disabled={!onClick}>
            <HeaderRow>
                <IconAndTitle>
                    <IconWrapper tint={display.iconColor}>
                        <Icon fontSize="small" />
                    </IconWrapper>
                    <Typography variant="subtitle1" fontWeight={600}>
                        {display.displayName}
                    </Typography>
                </IconAndTitle>
                <KeyboardArrowRightIcon color="action" fontSize="small" />
            </HeaderRow>
            <Description variant="body2">{display.description}</Description>
            <Footer>
                <FooterItem>
                    <PersonOutlineIcon fontSize="small" />
                    {userCount} {userCount === 1 ? 'user' : 'users'}
                </FooterItem>
                <FooterItem>
                    <VisibilityOutlinedIcon fontSize="small" />
                    {accessiblePages}/{totalPages} permissions
                </FooterItem>
            </Footer>
        </Card>
    );
};
